import { convertTimeDurationToSeconds, convertTimeOfDayToSeconds } from "../utils/time";
import { ClockGraphic, Lobbies, PeekRoomInfo, PhaseDefinition, PhaseType, RoomSchema, ScheduledSession, SessionSchema, TWAConfig, TimeString } from "./config";

/**
 * Internal object, created out of the TWAConfig that is defined as a json.
 * This has all TimeStrings converted to seconds, all references resolved and all defaults applied.
 */
export interface ParsedTWAConfig {
    timezone: string;
    /** a list of all formats that (each!) audio is available in. Default: [".mp3"]. Add e.g. ".aac" for better playback in Safari*/
    audioFormats: string[],
    zoom: {
        buttonVisible_beforeSessionStart?: number,
        autoJoin_beforeSessionStart?: number,
        autoJoin_afterSessionStart?: number,
    },
    individualTimer: IndivTimerSettings,
    /** definitions of room schemas */
    rooms: {
        [roomName: string]: Room;
    };
    startPage?: "lobby" | "roomlist" | "timer";
    lobbies: Lobbies;
}


export interface IndivTimerSettings extends Session {
    maxFocusDuration: number;
    minFocusDuration: number;
    defaultFocusDuration: number;
    focusDurationStep: number;
    pauseImages?: string[];
    color?: string;
}


export type Room = Omit<RoomSchema, "schedule"> & {
    name: string;
    schedule: Session[];
    peek: PeekRoomInfo;
}

export interface Session {
    /**
     * startTime in seconds since midnight (of config.timezone)
     */
    startTime: number;
    /**
     * endTime in seconds since midnight (of config.timezone)
     * NOTE: this might be larger than 24*60*60, if the ``Session`` starts before midnight and ends after midnight
     */
    endTime: number;
    duration: number;
    name: string;
    graphic: ClockGraphic<number>;
    audioFlavors: string[];
    phases: Phase[];
}

export interface Phase {
    name: string;
    description?: string;
    /**
     * startTime in seconds since midnight (of config.timezone)
     * NOTE: this might be larger than 24 * 60 * 60, if the ``Session`` starts before midnight and this ``Phase`` starts after midnight
     */
    startTime: number;
    /**
     * endTime in seconds since midnight (of config.timezone)
     * NOTE: this might be larger than 24 * 60 * 60, if the ``Session`` starts before midnight and this ``Phase`` ends after midnight
     */
    endTime: number;
    duration: number;
    maySkipAfter?: number;
    imageInsteadOfClock?: string;
    color: string;
    /** This is a list of audios, each consisting of a several formats (depending on the config.audioFormats).
     * E.g. 
     * ```
     * [
     *    ["session_intro_wiebke.aac", "session_intro_wiebke.mp3"], 
     *    ["session_intro_ingrid.aac", "session_intro_ingrid.mp3"]
     * ]
     * ```
     */
    audioFormats: string[][] | null;
    /** This is a list of audios (without format extensions)
     * E.g.
     * ```
     * ["session_intro_wiebke", "session_intro_ingrid"]
     * ```
     */
    audio: string[] | null;
    hiddenOnClock?: boolean;
    type: PhaseType;
}

export const parseConfig = (config: TWAConfig): ParsedTWAConfig => {
    const parsedConfig: ParsedTWAConfig = {
        timezone: config.timezone,
        audioFormats: config.audioFormats || [".mp3"],
        zoom: {
            buttonVisible_beforeSessionStart: convertTimeDurationToSeconds(config.zoom.buttonVisible_beforeSessionStart),
            autoJoin_beforeSessionStart: convertTimeDurationToSeconds(config.zoom.autoJoin_beforeSessionStart),
            autoJoin_afterSessionStart: convertTimeDurationToSeconds(config.zoom.autoJoin_afterSessionStart),
        },
        individualTimer: parseIndivTimerSettings(config),
        rooms: {},
        startPage: config.startPage,
        lobbies: config.lobbies,
    };
    for (const roomName in config.rooms) {
        parsedConfig.rooms[roomName] = parseRoom(roomName, config.rooms[roomName], config);
    }
    console.debug("parsedConfig", { config, parsedConfig });
    return parsedConfig;
}

const parseIndivTimerSettings = (config: TWAConfig): IndivTimerSettings => {
    const s = parseSession({ sessionName: "", sessionSchema: config.individualTimer.sessionSchema, startTime: "00:00" as TimeString }, config);
    return {
        ...s,
        maxFocusDuration: convertTimeDurationToSeconds(config.individualTimer.maxFocusDuration),
        minFocusDuration: convertTimeDurationToSeconds(config.individualTimer.minFocusDuration),
        defaultFocusDuration: convertTimeDurationToSeconds(config.individualTimer.defaultFocusDuration),
        focusDurationStep: convertTimeDurationToSeconds(config.individualTimer.focusDurationStep),
        pauseImages: config.individualTimer.pauseImages,
        color: config.individualTimer.color,
    };
}

const parseRoom = (name: string, room: RoomSchema, config: TWAConfig): Room => {
    return {
        ...room,
        peek: { //DEFAULT PeekRoomInfo
            buttonText: "Zum " + room.displayName,
            imageSession: "/img/zoom-gallery.png",
            // imageSessionHover: "/img/zoom-gallery-hover.png",
            imagePause: "/img/Schreibtisch.png",
            // imagePauseHover: "/img/Schreibtisch-hover.png",
            ...(room.peek || {}),
        },
        name,
        schedule: room.schedule.map(s => parseSession(s, config)).sort((a, b) => a.startTime - b.startTime)
    }
}

const parseSession = (session: ScheduledSession, config: TWAConfig): Session => {
    const sessionSchema = config.sessionSchemas[session.sessionSchema];
    if (!sessionSchema) throw new Error(`Session schema ${session.sessionSchema} not found`);

    const startTime = convertTimeOfDayToSeconds(session.startTime);
    const duration = convertTimeDurationToSeconds(sessionSchema.totalDuration);
    const endTime = startTime + duration;
    const s: Session = {
        startTime,
        endTime,
        duration,
        name: session.sessionName,
        graphic: {
            ...sessionSchema.graphic,
            fullCircleDuration: convertTimeDurationToSeconds(sessionSchema.graphic.fullCircleDuration),
        },
        audioFlavors: sessionSchema.audioFlavors || [],
        phases: [] as Phase[]
    };
    let phaseStartTime = startTime;
    for (const phase of sessionSchema.phases) {
        const p = parsePhase(phase, sessionSchema, config, phaseStartTime);
        phaseStartTime = p.endTime;
        s.phases.push(p);
    }
    return s;
}

const parsePhase = (phase: PhaseDefinition, sessionSchema: SessionSchema, config: TWAConfig, startTime: number): Phase => {
    const duration = convertTimeDurationToSeconds(phase.duration);
    const maySkipAfter = phase.maySkipAfter ? convertTimeDurationToSeconds(phase.maySkipAfter) : undefined;
    const endTime = startTime + duration;
    const containsAudio = phase.audioBaseName || phase.audio;
    return {
        type: containsAudio ? "audio" : "focus",
        ...phase,
        startTime,
        endTime,
        duration,
        maySkipAfter,
        hiddenOnClock: phase.hiddenOnClock,
        audioFormats: createAudioFormatsArray(phase.audioBaseName, phase.audio, sessionSchema.audioFlavors, config.audioFormats),
        audio: createAudioArray(phase.audioBaseName, phase.audio, sessionSchema.audioFlavors)
    };
}

const createAudioFormatsArray = (audioBaseName: string | null, audio: string | null, audioFlavors: string[] | undefined, audioFormats: string[]): string[][] | null  => {
    if (audio) {
        const audioAlreadyContainsFormat = audioFormats.some(format => audio.toLowerCase().endsWith(format.toLowerCase()));
        return (audioFlavors || [""]).map(flavor => //ignore flavor!
            audioFormats
                .map(format => format.startsWith('.') ? format : '.' + format)
                .map(format => audioAlreadyContainsFormat ? audio : `${audio}${format}`)
        ) || [];
    } else if (audioBaseName) {
        return (audioFlavors || [""]).map(flavor =>
            audioFormats
                .map(format => format.startsWith('.') ? format : '.' + format)
                .map(format => `${audioBaseName}${flavor}${format}`)
        ) || [];
    } else return null;
}

const createAudioArray = (audioBaseName: string | null, audio: string | null, audioFlavors: string[] | undefined): string[] | null => {
    if (audio) {
        return (audioFlavors || [""]).map(flavor => //ignore flavor!
            `${audio}`
        );
    } else if (audioBaseName) {
        return (audioFlavors || [""]).map(flavor =>
            `${audioBaseName}${flavor}`
        );
    } else return null;
}